// File: company-directory.component.ts (v4)
// Angular + PrimeNG directory using your DirectoryUser model + DirectoryService signals
// - Fixes TS error (unknown[] → string[]) with a proper type guard
// - Offices derived from UserRoles[].PermRoles.Franchises?.Title (array)
// - Cards grouped by office; people appear under every assigned office
// - Table shows multiple offices as tags and keeps the v1 styling

import { Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { ChipModule } from 'primeng/chip';
import { TagModule } from 'primeng/tag';
import { AzureUser, CardItem, DirectoryUser, JobTag, ViewPerson, Office } from '@/models/directory-user.model';
import { DirectoryService } from '@/services/api/directory.service';
import { AvatarModule } from 'primeng/avatar';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { DirectoryFormComponent } from './directory-form.component';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { isCompanyEmail } from '@/utility/emailCheck';
import { MultiSelectModule } from 'primeng/multiselect';
import DirectoryBulkPanelComponent from './directory.bulk-panel.component';
import { ToolbarModule } from 'primeng/toolbar';
import { PayloadGroupsService } from '@/services/api/payload-groups.service';
import { DirectoryAccessService } from '@/services/app/directory-access.service';
import { OfficesService } from '@/services/api/franchises.service';


/** Internal flattened view model */


function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

@Component({
  selector: 'app-company-directory',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    InputTextModule,
    SelectModule,
    ButtonModule,
    TableModule,
    DialogModule,
    ChipModule,
    TagModule,
    AvatarModule,
    MenuModule,
    DirectoryFormComponent,
    ToastModule,
    ConfirmDialogModule,
    IconFieldModule,
    InputIconModule,
    MultiSelectModule,
    DirectoryBulkPanelComponent,
    ToolbarModule
  ],
  styles: [`
    .pill { padding: .125rem .5rem; border-radius: 999px; font-size: .8rem; border: 1px solid var(--p-content-border-color); margin-right: .5rem; }
    .chips { display:flex; flex-wrap:wrap; gap:.5rem; }
    .group-title { margin: 1rem .25rem .5rem; font-size:.8rem; letter-spacing:.12em; text-transform:uppercase; color: var(--p-text-muted-color); }
    :host ::ng-deep .p-avatar img { border-radius: var(--border-radius) !important; }
    :host ::ng-deep .p-avatar { border-radius: var(--border-radius) !important; }
    :host ::ng-deep .p-card-body { padding: 0.5rem; }

    .cards-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 1rem; }
    .card-head { display:flex; align-items:center; justify-content:space-between; gap:.5rem; }

    /* Fix table row height consistency - ensure all cells in a row have the same height */
    /* All table cells maintain table-cell display so they automatically stretch to same height */
    :host ::ng-deep .p-datatable .p-datatable-tbody > tr > td {
      display: table-cell;
      padding: 0.75rem;
    }
    /* Single-line content columns: vertically center the content within the stretched cell */
    :host ::ng-deep .p-datatable .p-datatable-tbody > tr > td:first-child,
    :host ::ng-deep .p-datatable .p-datatable-tbody > tr > td:nth-child(2),
    :host ::ng-deep .p-datatable .p-datatable-tbody > tr > td:nth-child(3),
    :host ::ng-deep .p-datatable .p-datatable-tbody > tr > td:nth-child(6),
    :host ::ng-deep .p-datatable .p-datatable-tbody > tr > td:nth-child(7),
    :host ::ng-deep .p-datatable .p-datatable-tbody > tr > td:nth-child(8) {
      vertical-align: middle;
    }
    /* Multi-line content columns (Office, Responsibilities): top align - these determine row height */
    :host ::ng-deep .p-datatable .p-datatable-tbody > tr > td:nth-child(4),
    :host ::ng-deep .p-datatable .p-datatable-tbody > tr > td:nth-child(5) {
      vertical-align: top;
      line-height: 1.5;
    }
    /* Name column: flex container inside cell centers content vertically */
    :host ::ng-deep .p-datatable .p-datatable-tbody > tr > td:nth-child(2) .flex.align-items-center {
      display: flex;
      align-items: center;
      height: 100%;
    }
    /* Ensure tag containers wrap properly */
    :host ::ng-deep .p-datatable .p-datatable-tbody > tr > td p-tag {
      display: inline-block;
      margin-right: 0.25rem;
      margin-bottom: 0.25rem;
      vertical-align: top;
    }
  `],
  template: `
  <div class="p-4 card">
    <div class="flex justify-content-between align-items-center mb-3 ">
      <h2 class="m-0 text-2xl font-bold">User Settings</h2>
      
    </div>
    <p-toolbar class="mb-3">
    <ng-template #start>
    <p-iconfield>
    <p-inputicon styleClass="pi pi-search" />
    <input type="text" pInputText placeholder="Search" [(ngModel)]="search"/>
</p-iconfield>
<p-select [options]="sortOptions" [(ngModel)]="sort" class="ml-2"></p-select>

    </ng-template>

    <ng-template #center>
    <button pButton type="button" (click)="toggleView()" [label]="view() === 'cards' ? 'Cards View' : 'Table View'" class="p-button-text"></button>
    <button pButton type="button" (click)="clearFilters()" label="Clear Filters" class="p-button-outlined ml-2"></button>
    </ng-template>
    <ng-template #end>
    
    <directory-bulk-panel></directory-bulk-panel>
    <p-button label="Add New" icon="pi pi-plus" (click)="addNew()" class="ml-2" />
    </ng-template>
</p-toolbar>
      <div class="col-3 md:col-2">
      </div>
@if(directoryAccessService.globalUnlocked()) {
    <div class="mb-3">
      <strong>Filter by Office:</strong>
      <div class="chips mt-2">
      <p-button *ngFor="let office of offices()" [label]="office.name" [severity]="selectedOffices().has(office.name) ? 'primary' : 'secondary'" (click)="toggleOffice(office.name)" />
      <p-button [label]="'Unassigned'" [severity]="selectedOffices().has('Unassigned') ? 'primary' : 'secondary'" (click)="toggleNoOffice()" />
      <!-- <p-chip *ngFor="let office of offices()" [label]="office" (click)="toggleOffice(office)"
      [styleClass]="selectedOffices().has(office) ? 'bg-primary cursor-pointer text-white' : ''"></p-chip> -->
      </div>
    </div>

    <div class="mb-3">
      <strong>Filter by Responsibility:</strong>
      <div class="chips mt-2">
      <p-button *ngFor="let t of tags()" [label]="t"[severity]="selectedTags().has(t) ? 'primary' : 'secondary'" (click)="toggleTag(t)" />
      </div>
    </div>
}
    <!-- Results -->
    <ng-container *ngIf="view() === 'cards'; else tableView">
      <ng-container *ngFor="let group of groupedByOffice()">
        <h3 class="group-title">{{ group.office }}</h3>
        <div class="cards-grid">
          <div *ngFor="let p of group.rows">
            <p-card class="shadow-1">
              <ng-template pTemplate="header">
                <div class="card-head p-2">
                  <div class="flex align-items-center">
                    <div class="flex align-items-center justify-content-center mr-2" style="width:40px;height:40px;">
                      <p-avatar *ngIf="p.photoUrl; else initAvatar" size="large" [image]="p.photoUrl" />
                      <ng-template #initAvatar><p-avatar [label]="initials(p.name)" size="large" /></ng-template>
                    </div>
                    <div>
                      <div class="font-bold">{{ p.name }}</div>
                      <small class="text-500">{{ p.title }}</small>
                    </div>
                  </div>

                  <!-- Ellipsis menu -->
                  <div class="flex align-items-center">
                    <p-menu #menu [popup]="true" [model]="getCardMenu(p)"></p-menu>
                    <button pButton type="button" icon="pi pi-ellipsis-v" class="p-button-text p-button-rounded p-button-plain"
                            (click)="menu.toggle($event)" aria-label="More"></button>
                  </div>
                </div>
              </ng-template>

              <div>
                <!-- Office already shown by group header -->
                <div class="flex flex-wrap gap-2 mb-2">
                  <p-tag *ngFor="let t of p.tags" [value]="t" (click)="toggleTag(t)" styleClass="cursor-pointer"></p-tag>
                </div>
              </div>

              <ng-template pTemplate="footer">
                <div class="flex gap-2">
                  <a pButton type="button" [href]="'mailto:' + p.email" label="Email" class="p-button-text"></a>
                  <a pButton type="button" [href]="'tel:' + p.phone" label="Call" class="p-button-text"></a>
                  <button pButton type="button" (click)="openDialog(p)" label="Details" class="p-button-text"></button>
                  <button pButton type="button" (click)="confirmArchive(p)" label="Archive" class="p-button-text"></button>
                </div>
              </ng-template>
            </p-card>
          </div>
        </div>
      </ng-container>
    </ng-container>

    <!-- Table view matching first iteration styling; Office shows tags when multiple -->
    <ng-template #tableView>
      <p-table 
      [value]="filteredPeople()" 
      [tableStyle]="{ 'min-width': '50rem' }" 
      [globalFilterFields]="['offices']"
      dataKey="id" 
      [(selection)]="bulkUsers">
        <ng-template pTemplate="header">
          <tr>
          <th style="width: 4rem"><p-tableHeaderCheckbox /></th>
            <th>Name</th>
            <th>Title</th>
            <th>
            <div class="flex items-center justify-between">
                   Office
                    <p-columnFilter field="offices" matchMode="in" display="menu" [showMatchModes]="false" [showOperator]="false" [showAddButton]="false">
                        <ng-template #filter let-value let-filter="filterCallback">
                            <p-multiselect [ngModel]="value" [options]="directoryService.officePerms()" placeholder="Any" (ngModelChange)="filter($event)" optionLabel="Franchises.CityCode">
                            </p-multiselect>
                        </ng-template>
                    </p-columnFilter>
                </div>

            </th>
            <th>Responsibilities</th>
            <th>Email</th>
            <th>Phone</th>
            <th></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-p>
          <tr>
          <td>
                    <p-tableCheckbox [value]="p" />
                </td>
            <td>
            <div class="flex align-items-center">
            <p-avatar *ngIf="p.photoUrl; else initAvatar" [image]="p.photoUrl" class="mr-2" />
            <ng-template #initAvatar><p-avatar [label]="initials(p.name)" class="mr-2"/></ng-template>
            {{ p.name }}
            </div>
            </td>
            <td>{{ p.title }}</td>
            <td>
              <p-tag *ngFor="let o of p.offices" [value]="o.name" styleClass="mr-1 mb-1"></p-tag>
            </td>
            <td>
              <p-tag *ngFor="let t of p.tags" [value]="t" styleClass="mr-1 mb-1 cursor-pointer" (click)="toggleTag(t)"></p-tag>
            </td>
            <td><a [href]="'mailto:' + p.email">{{ p.email }}</a></td>
            <td><a [href]="'tel:' + p.phone">{{ p.phone }}</a></td>
            <td class="text-right">
              <button pButton icon="pi pi-pencil" size="small" (click)="openDialog(p)" class="p-button-text"></button>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </ng-template>
    <p-toast />
    <p-confirmdialog />
    <!-- Details dialog -->
    <p-dialog [(visible)]="showDialog" [modal]="true" >
      <!-- <ng-template pTemplate="header">{{ selectedPerson?.name }}</ng-template>
      <div *ngIf="selectedPerson as sp">
        <div class="font-bold mb-2">{{ sp.title }}</div>
        <div class="mb-2">
          <p-tag *ngFor="let o of sp.offices" [value]="o" class="mr-1" />
        </div>
        <div class="mb-2">
          <p-tag *ngFor="let t of sp.tags" [value]="t" class="mr-1" />
        </div>
        <p>Email: <a [href]="'mailto:' + sp.email">{{ sp.email }}</a></p>
        <p>Phone: <a [href]="'tel:' + sp.phone">{{ sp.phone }}</a></p>
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" label="Close" (click)="showDialog=false"></button>
      </ng-template> -->
      <app-directory-form></app-directory-form>
    </p-dialog>
    <p-dialog [(visible)]="showAddNew" [modal]="true" >
      
<p-iconfield>
    <p-inputicon [class]="directoryService.loadingSearch() ? 'pi pi-spinner pi-spin' : 'pi pi-search'" />
    <input type="text" pInputText placeholder="Search"  [(ngModel)]="newSearch"/>
</p-iconfield>

<button pButton *ngIf="foundLocalUser()" [severity]="'secondary'" (click)="openResult()" style="justify-content: flex-start !important;" class="col-span-12 md:col-span-9 ml-2 border-round shadow p-2 mb-1 text-left" style="border-radius: var(--border-radius);">
            <div class="flex justify-between items-center w-full">
                <div class="flex-1 text-left">
                    <div class="block text-900 text-left mb-1">{{getUserName(directoryService.result())}}</div>
                    <div class="block text-900 text-left">{{getUserTitle(directoryService.result())}}</div>
                </div>
                <div class="flex items-center ml-2 mr-2">
                    <i class="pi pi-pencil" style="color:var(--primary-color);"></i>
                </div>
            </div>
</button>

<button pButton *ngIf="foundAzureUser()" [severity]="'secondary'" (click)="convertResult()" style="justify-content: flex-start !important;" class="col-span-12 md:col-span-9 ml-2 border-round shadow p-2 mb-1 text-left" style="border-radius: var(--border-radius);">
            <div class="flex justify-between items-center w-full">
                <div class="flex-1 text-left">
                    <div class="block text-900 text-left mb-1">{{getUserName(directoryService.result())}}</div>
                    <div class="block text-900 text-left">{{getUserTitle(directoryService.result())}}</div>
                </div>
                <div class="flex items-center ml-2 mr-2">
                    <i class="pi pi-pencil" style="color:var(--primary-color);"></i>
                </div>
            </div>
</button>
    </p-dialog>
  </div>
  `
})
export default class AppDirectoryComponent {
  directoryService = inject(DirectoryService);
  confirmationService = inject(ConfirmationService);
  payloadGroupsService = inject(PayloadGroupsService);
  directoryAccessService = inject(DirectoryAccessService);
  officesService = inject(OfficesService);
  bulkTag:JobTag | null = null;
  showAddNew = false;
  sortOptions = [
    { label: 'Sort: Name (A→Z)', value: 'name' },
    { label: 'Sort: Title (A→Z)', value: 'title' },
    { label: 'Sort: Office (A→Z)', value: 'office' }
  ];

    // -- UI state --
    readonly search = signal('');
    readonly newSearch = signal('');
    readonly sort = signal<'name' | 'title' | 'office'>('name');
    readonly view = signal<'cards' | 'table'>('table');
    readonly selectedOffices = signal(new Set<string>());
    readonly selectedTags = signal(new Set<string>());
  
    readonly showDialog = signal(false);
    readonly selectedPerson = signal<ViewPerson | null>(null);

    unassignedSelected = false;

  constructor() {
    effect(() => {
      const newSearch = this.newSearch();
      if(newSearch && isCompanyEmail(newSearch)) {
        this.searchAzureUser();
      }
    });
  }

  // Access data from service signals
  get users() { return this.directoryService.userList(); }

  get availableTags() { return this.directoryService.availableTags(); }

  get bulkUsers() { return this.directoryService.bulkUsers(); }

  set bulkUsers(users: ViewPerson[] | []) { this.directoryService.bulkUsers.set(users); }

  // Accessors — customize to your system
  getName = (u: DirectoryUser) => u.DisplayName || `${u.FirstName ?? ''} ${u.LastName ?? ''}`.trim();
  getTitle = (u: DirectoryUser) => u.Title ?? '';
  getEmail = (u: DirectoryUser) => u.User ?? '';
  getPhone = (u: DirectoryUser) => {
    const p = (u.user_phones || []).find(ph => ph.is_primary && ph.is_active) || (u.user_phones || [])[0];
    return p?.number ?? '';
  };
  // — Office derivation from UserRoles (array)
  getOffices = (u: DirectoryUser): Office[] => {
    const roles = (u as any)?.UserRoles as unknown[] | undefined;
    if (!Array.isArray(roles)) return [];
    const offices = roles
      .map((r: any) => {
        const fid = r?.PermRoles?.FID;
        // Skip location roles
        // if (r?.PermRoles?.location === true) return null;
        // Skip if FID is null (global roles)
        if (fid === null || fid === undefined) return null;
        
        // Try to get title from PermRoles.Franchises first
        let title = r?.PermRoles?.Franchises?.Title;
        
        // If title is missing, try to look it up from OfficesService
        if (!isNonEmptyString(title) && typeof fid === 'number') {
          const franchise = this.officesService.lookupFranchiseById(fid);
          if (franchise?.Title) {
            title = franchise.Title;
          }
        }
        
        // Also try to get from officePerms if available
        if (!isNonEmptyString(title) && typeof fid === 'number') {
          const permRole = this.directoryService.officePerms().find(p => p.FID === fid);
          if (permRole?.Franchises?.Title) {
            title = permRole.Franchises.Title;
          }
        }
        
        if (isNonEmptyString(title) && typeof fid === 'number') {
          return { name: title.trim(), FID: fid };
        }
        return null;
      })
      .filter((o): o is Office => o !== null);
    // de-dup by FID & sort by name
    const uniqueOffices = new Map<number, Office>();
    offices.forEach(office => {
      if (!uniqueOffices.has(office.FID)) {
        uniqueOffices.set(office.FID, office);
      }
    });
    return Array.from(uniqueOffices.values()).sort((a, b) => a.name.localeCompare(b.name));
  };
  getTags = (u: DirectoryUser) => (u.user_tags_user_tags_uidToUsers || [])
    .filter(t => t.jobTags?.is_active !== false)
    .map(t => t.jobTags?.name)
    .filter((x): x is string => !!x);
  getManager = (u: DirectoryUser) => u.Users_Users_manager_uidToUsers?.DisplayName;
  getPhoto = (u: DirectoryUser) => u.photoUrl || '';


  items: MenuItem[] = [
    {
        label: 'Options',
        items: [
            {
                label: 'Refresh',
                icon: 'pi pi-refresh'
            },
            {
                label: 'Export',
                icon: 'pi pi-upload'
            }
        ]
    }
];

  addNew() {
    this.directoryService.clearResult();
    this.newSearch.set('');
    this.showAddNew = true;
  }

  getUserName(user: DirectoryUser | AzureUser | null) {
    if(!user) {
      return '';
    }
    return (user as DirectoryUser).DisplayName || (user as AzureUser).displayName || '';
  }

  openResult() {
    this.showAddNew = false;
    this.directoryService.setSelectedUser((this.directoryService.result() as DirectoryUser).UID);
    this.showDialog.set(true);
  }

  convertResult() {

  }

  getUserTitle(user: DirectoryUser | AzureUser | null) {
    if(!user) {
      return '';
    }
    return (user as DirectoryUser).Title || (user as AzureUser).jobTitle || '';
  }

  foundLocalUser() {
    return this.directoryService.result() !== null && (this.directoryService.result() as DirectoryUser).UID !== undefined;
  }

  foundAzureUser() {
    return !this.foundLocalUser() && this.directoryService.result() !== null && (this.directoryService.result() as AzureUser).id !== undefined;
  }

  searchAzureUser() {
    this.directoryService.findUser(this.newSearch());
  }

  // Facets
  readonly offices = computed(() => {
    const all = this.users.flatMap(u => this.getOffices(u));
    const uniqueOffices = new Map<number, Office>();
    all.forEach(office => {
      if (!uniqueOffices.has(office.FID)) {
        uniqueOffices.set(office.FID, office);
      }
    });
    const offices = Array.from(uniqueOffices.values()).sort((a, b) => a.name.localeCompare(b.name));
    return offices;
  });

  readonly tags = computed(() => {
    return [...new Set(this.users.flatMap(u => this.getTags(u)))].sort((a,b) => a.localeCompare(b));
  });

  // Transform + filter
  private toView(u: DirectoryUser): ViewPerson {
    const offices = this.getOffices(u);
    return {
      id: u.UID,
      name: this.getName(u) || '(No name)',
      title: this.getTitle(u),
      email: this.getEmail(u),
      phone: this.sanitizePhone(this.getPhone(u)),
      offices: offices.length ? offices : [{ name: 'Unassigned', FID: 0 }],
      tags: this.getTags(u),
      photoUrl: this.getPhoto(u),
      manager: this.getManager(u),
      isAlias: !!u.Users_Users_alias_of_uidToUsers?.UID
    };
  }

 // Step 1: Flatten and transform the raw data into a memoized signal
 readonly allPeople = computed(() => this.users.map(u => this.toView(u)));

 // Step 2: Create a computed signal for filtering and sorting
 readonly filteredPeople = computed(() => {
   const q = this.search().trim().toLowerCase();
   const selectedOffices = this.selectedOffices();
   const selectedTags = this.selectedTags();
   const currentSort = this.sort();
   let list = [];

  list = this.allPeople();

   if (q) {
     list = list.filter(p =>
       p.name.toLowerCase().includes(q) ||
       (p.title || '').toLowerCase().includes(q) ||
       (p.email || '').toLowerCase().includes(q) ||
       (p.phone || '').toLowerCase().includes(q) ||
       p.offices.some(o => o.name.toLowerCase().includes(q)) ||
       (p.tags || []).some(t => t.toLowerCase().includes(q))
     );
   }

   // Apply office filter
   if (selectedOffices.size) {
     list = list.filter(p => p.offices.some(o => selectedOffices.has(o.name)));
   }

   // Apply tag filter
   if (selectedTags.size) {
     list = list.filter(p => [...selectedTags].every(t => p.tags.includes(t)));
   }

   // Apply sorting
   if (currentSort === 'name') {
     list.sort((a, b) => a.name.localeCompare(b.name));
   } else if (currentSort === 'title') {
     list.sort((a, b) => a.title.localeCompare(b.title) || a.name.localeCompare(b.name));
   } else if (currentSort === 'office') {
     list.sort((a, b) => (a.offices[0]?.name ?? '').localeCompare(b.offices[0]?.name ?? '') || a.name.localeCompare(b.name));
   }

   return list;
 });

 // Step 3: Create a computed signal for the grouped card view
 readonly groupedByOffice = computed(() => {
   const groups = new Map<string, CardItem[]>();
   for (const p of this.filteredPeople()) {
     for (const o of p.offices) {
       const key = o.name || 'Unassigned';
       if (!groups.has(key)) groups.set(key, []);
       groups.get(key)!.push({ ...p, displayOffice: key });
     }
   }
   return Array.from(groups.entries())
     .sort((a, b) => a[0].localeCompare(b[0]))
     .map(([office, rows]) => ({ office, rows }));
 });

  confirmArchive(p: ViewPerson) {
    console.log('confirm archive', p);
    this.confirmationService.confirm({
      message: 'Are you sure you want to archive this user?',
      accept: () => this.archive(p)
    });
  }

  // ——— Actions
  toggleView() { this.view.update(v => v === 'cards' ? 'table' : 'cards'); }
  archive(p: ViewPerson) { 
    console.log('archive', p); 
    this.directoryService.archiveUser(p.id);
  }
  clearFilters() {
    this.search.set('');
    this.selectedOffices.set(new Set());
    this.selectedTags.set(new Set());
  }
  toggleOffice(o: string) {
    this.selectedOffices.update(current => {
      current.has(o) ? current.delete(o) : current.add(o);
      return new Set(current); // Must return a new Set to trigger the signal
    });
  }
  toggleNoOffice() {
    const o = 'Unassigned';
    this.selectedOffices.update(current => {
      current.has(o) ? current.delete(o) : current.add(o);
      return new Set(current); // Must return a new Set to trigger the signal
    });
  }
  toggleTag(t: string) {
    this.selectedTags.update(current => {
      current.has(t) ? current.delete(t) : current.add(t);
      return new Set(current); // Must return a new Set to trigger the signal
    });
  }
  openDialog(p: ViewPerson) {
    const user = this.directoryService.userList().find(u => u.UID === p.id);
    this.directoryService.rolesList.set([]);
    if(user) {
      this.directoryService.populateLocalPermsList(user);
    }
    this.selectedPerson.set(p);
    this.directoryService.setSelectedUser(p.id);
    this.showDialog.set(true);
  }

  // Card menu model
  getCardMenu(p: ViewPerson): MenuItem[] {
    return  [
      { label: 'Archive', icon: 'pi pi-eye-slash', command: () => this.confirmArchive(p) },
      { label: 'View details', icon: 'pi pi-info-circle', command: () => this.openDialog(p) },
      { separator: true },
      { label: 'Email', icon: 'pi pi-envelope', command: () => window.open(`mailto:${p.email}`, '_self') },
      
    ];
    // if (p.phone) items.push({ label: 'Call', icon: 'pi pi-phone', command: () => window.open(`tel:${p.phone}`, '_self') });
    // items.push({ label: 'Copy email', icon: 'pi pi-copy', command: () => this.copy(p.email) });
    // if (p.phone) items.push({ label: 'Copy phone', icon: 'pi pi-copy', command: () => this.copy(p.phone) });
    // return items;
  }

  async copy(text: string) {
    try { await navigator.clipboard.writeText(text); } catch { /* no-op */ }
  }

  initials(name: string) { return name.split(/\s+/).slice(0,2).map(s => s[0]).join('').toUpperCase(); }
  sanitizePhone(phone: string) { return (phone || '').replace(/[^\d+]/g, ''); }
}
